import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { MDBBtn, MDBIcon } from "mdb-react-ui-kit";
import * as XLSX from "xlsx";
import ApiService from "../../Core/Service/ApiService";
import AdminTable from "../../Components/AdminTable/AdminTable";
import Paginator from "../../Components/Paginator/Paginator";
import LoadingIndicator from "../../Components/LoadingIndicator/LoadingIndicator";
import CmsContext from "../../Context/CmsContext";

const perPageOptions = [
    { value: 10, label: "10" },
    { value: 20, label: "20" },
    { value: 50, label: "50" },
    { value: 100, label: "100" },
];

const columns = [
    { name: "prod_code", title: "Product Code", sortable: true, search: { enable: true, type: "text", ops: [{ value: "like", label: "Like" }] } },
    { name: "prod_desc", title: "Description", sortable: true, search: { enable: true, type: "text", ops: [{ value: "like", label: "Like" }] } },
    { name: "brand", title: "Brand", sortable: true, search: { enable: true, type: "text", ops: [{ value: "like", label: "Like" }] } },
    { name: "parent_category", title: "Category", sortable: true, search: { enable: true, type: "text", ops: [{ value: "like", label: "Like" }] } },
    { name: "wholesale", title: "Wholesale Barcode", sortable: true, search: { enable: true, type: "text", ops: [{ value: "like", label: "Like" }] } },
    { name: "retail", title: "Retail Barcode", sortable: true, search: { enable: true, type: "text", ops: [{ value: "like", label: "Like" }] } },
];

const MissingImages = React.memo(() => {
    const cmsContext = useContext(CmsContext);
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [pageNum, setPageNum] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [search, setSearch] = useState({});
    const [sort, setSort] = useState({ name: "prod_code", dir: "ASC" });

    useEffect(() => {
        cmsContext.setBreadcrumb([
            { title: cmsContext.currMainMenu?.label ?? "" },
            { title: cmsContext.currSubMenu?.label ?? "" },
        ]);
    }, []);

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await ApiService.get("/products/missing-images");
            setProducts(response.data.products || []);
        } finally {
            setLoading(false);
        }
    };

    const filteredSortedProducts = useMemo(() => {
        let result = products;

        // Filter
        Object.keys(search).forEach((key) => {
            const s = search[key];
            if (!s || !s.op || s.value === undefined || s.value === "") return;
            const needle = String(s.value).toLowerCase();
            result = result.filter((row) => {
                const v = row[key];
                if (v === null || v === undefined) return false;
                return String(v).toLowerCase().includes(needle);
            });
        });

        // Sort
        if (sort?.name) {
            const dir = sort.dir === "DESC" ? -1 : 1;
            result = [...result].sort((a, b) => {
                const av = a[sort.name] ?? "";
                const bv = b[sort.name] ?? "";
                if (av < bv) return -1 * dir;
                if (av > bv) return 1 * dir;
                return 0;
            });
        }

        return result;
    }, [products, search, sort]);

    const pagedProducts = useMemo(() => {
        const start = (pageNum - 1) * perPage;
        return filteredSortedProducts.slice(start, start + perPage);
    }, [filteredSortedProducts, pageNum, perPage]);

    const debounceTimer = useRef(null);
    const handleSearch = useCallback((s) => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            setSearch(s);
            setPageNum(1);
        }, 300);
    }, []);

    const handleSort = (s) => {
        setSort(s);
        setPageNum(1);
    };

    const exportToExcel = () => {
        const rows = filteredSortedProducts.map((p) => ({
            "Product Code": p.prod_code,
            Description: p.prod_desc,
            Brand: p.brand,
            Category: p.parent_category,
            "Wholesale Barcode": p.wholesale,
            "Retail Barcode": p.retail,
        }));
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Missing Images");
        const today = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `missing-images-${today}.xlsx`);
    };

    const renderTbodyTr = (row, rowIndex) => (
        <tr key={rowIndex}>
            {columns.map((col, colIndex) => (
                <td key={colIndex}>{row[col.name]}</td>
            ))}
        </tr>
    );

    return (
        <div className="missing-images-report">
            <div className="d-flex align-items-center ps-2">
                <label>Per Page:</label>
                <div className="ms-2" style={{ width: 100 }}>
                    <Select
                        options={perPageOptions}
                        value={perPageOptions.find((opt) => opt.value === perPage)}
                        onChange={(opt) => { setPerPage(opt.value); setPageNum(1); }}
                    />
                </div>
                <label className="ms-4">
                    Found
                    <span className="fw-bold mx-2" style={{ color: "#333" }}>{filteredSortedProducts.length}</span>
                    Matching Results.
                </label>
                <div className="ms-auto">
                    <MDBBtn onClick={exportToExcel} disabled={filteredSortedProducts.length === 0}>
                        <MDBIcon fas icon="file-excel" className="me-2" />
                        Export to Excel
                    </MDBBtn>
                </div>
            </div>

            <div className="mt-3">
                <AdminTable
                    columns={columns}
                    rows={pagedProducts}
                    renderTbodyTr={renderTbodyTr}
                    handleSearch={handleSearch}
                    handleSort={handleSort}
                    search={search}
                    sort={sort}
                    autoSearch
                />
            </div>

            <div className="d-flex justify-content-center mt-4">
                <Paginator curPage={pageNum} total={filteredSortedProducts.length} perPage={perPage} onPageChanged={setPageNum} />
            </div>

            <LoadingIndicator show={loading} />
        </div>
    );
});

export default MissingImages;
